import React, { useState } from 'react';
import InputModal from '../../../ui/inputModal/InputModal';
import Pagination from '../../../ui/pagination/Pagination';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Copy, SquarePen, Trash2, ClipboardPenLine, AlertTriangle } from 'lucide-react';
import Checkbox from '../../../ui/Checkbox/Checkbox';
import styles from './ToolsTable.module.css';
import { toast } from '../../../lib/toast';
import { toolsApi } from '../../../lib/apiClient';
import { useTagsStore } from '../../../store/tagsStore';
import { useAuthStore } from '../../../store/authStore';
import { McpTool } from '../../../types/types';
import { isToolComplete, getToolValidationError } from '../../../lib/toolCompletion';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';
import TruncatedText from '../../../ui/TruncatedText/TruncatedText';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { useToolsStore } from '../../../store/appStore';
import { Environment } from '../../../types/environment';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { TagsEnv } from '../../../ui/Tags/TagsEnv';

const NEW_BADGE_HOURS = 24;

function isNewTool(tool: McpTool): boolean {
    if (!tool.created_at) return false;
    const created = new Date(tool.created_at).getTime();
    return Date.now() - created < NEW_BADGE_HOURS * 60 * 60 * 1000;
}

interface ToolsTableProps {
    pageCount: number;
    setPage: (page: number) => void;
    filteredAndSortedTools: McpTool[];
    page: number;
    onTagClick?: (tag: string) => void;
    selectedToolIds?: number[];
    onSelectedToolIdsChange?: (ids: number[]) => void;
}

const ToolsTable: React.FC<ToolsTableProps> = ({ pageCount, setPage, filteredAndSortedTools, page, onTagClick, selectedToolIds, onSelectedToolIdsChange }) => {
    const user = useAuthStore(state => state.user)
    const refreshTagsFromTools = useTagsStore(state => state.refreshTagsFromTools)
    const connectors = useConnectionsStore(state => state.connectors);
    const { addTool, removeTool, tools, setTools } = useToolsStore(state => state);
    const [internalSelectedIds, setInternalSelectedIds] = useState<number[]>([]);
    const selectedIds = selectedToolIds ?? internalSelectedIds;
    const setSelectedIds = onSelectedToolIdsChange ?? setInternalSelectedIds;
    const [displayName, setDisplayName] = useState('');
    const [technicalName, setTechnicalName] = useState('');
    const [technicalNameEdited, setTechnicalNameEdited] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyToolId, setCopyToolId] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [deletingTools, setDeletingTools] = useState(false);
    const [duplicatingTool, setDuplicatingTool] = useState(false);
    const [expandedTagToolIds, setExpandedTagToolIds] = useState<Set<number>>(new Set());
    const canEdit = !!user && hasMinRole(user?.role?.id, UserRole.ToolManager);
    const canView = user?.role?.id === UserRole.PromptEditor;

    const handleSelect = (id: number, checked: boolean) => {
        if (!canEdit) return;
        setSelectedIds(checked ? [...selectedIds, id] : selectedIds.filter(i => i !== id));
    };

    const pageToolIds = filteredAndSortedTools.map(tool => tool.id);
    const allOnPageSelected = pageToolIds.length > 0 && pageToolIds.every(id => selectedIds.includes(id));

    const handleSelectAll = (checked:boolean) => {
        if (!canEdit) return;
        if (checked) {
            setSelectedIds([...selectedIds, ...pageToolIds.filter(id => !selectedIds.includes(id))]);
    } else {
        setSelectedIds(selectedIds.filter(id => !pageToolIds.includes(id)));
    }
};

    const handleBulkDelete = () => {
        if (!canEdit || selectedIds.length === 0) return;
        setDeleteIds(selectedIds);
        setShowDeleteModal(true);
    };

    const handleDelete = (toolId: number) => {
        if (!canEdit) return;
        setDeleteIds([toolId]);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!canEdit || deleteIds.length === 0 || deletingTools) return;
        const idsToDelete = [...deleteIds];
        try {
            setDeletingTools(true);
            if (idsToDelete.length === 1) {
                // single delete
                await toolsApi.delete(idsToDelete[0]);
                removeTool(idsToDelete[0]);
                toast.success('Tool deleted successfully');
                setSelectedIds(selectedIds.filter(id => id !== idsToDelete[0]));
                refreshTagsFromTools(tools.filter(t => t.id !== idsToDelete[0]))
            } else {
                await toolsApi.bulkDelete(idsToDelete);
                idsToDelete.forEach(id => removeTool(id));
                toast.success(idsToDelete.length > 1 ? 'Tools deleted successfully!' : 'Tool deleted successfully');
                setSelectedIds(selectedIds.filter(id => !idsToDelete.includes(id)));
                refreshTagsFromTools(tools.filter(t => !idsToDelete.includes(t.id)))
            }
        } catch (error: any) {
            toast.error(idsToDelete.length > 1 ? 'Bulk delete failed' : `Failed to delete tool: ${error.message}`);
        } finally {
            setDeletingTools(false);
            setShowDeleteModal(false);
            setDeleteIds([]);
        }
    };

    const toggleToolStatus = async (toolId: number, enabled: boolean) => {
        const tool = tools.find(t => t.id === toolId);
        if (tool) {
            const con = connectors.find(p => p.id === tool.mainConnectionId);
            if (enabled && (!con || !con.isActive)) {
                toast.error('Cannot enable tool: connector is inactive or not found');
                return;
            }
        }
        try {
            await toolsApi.update(toolId, { enabled })
            setTools(tools.map(tool =>
                tool.id === toolId ? { ...tool, enabled } : tool
            ))
            refreshTagsFromTools(tools)
            toast.success(`Tool ${enabled ? 'enabled' : 'disabled'}`)
        } catch (error: any) {
            toast.error(`Failed to update tool: ${error.message}`)
        }
    }

    const handleDuplicate = (toolId: number, displayName: string, technicalName: string) => {
        setCopyToolId(toolId);
        setDisplayName(displayName);
        setTechnicalName(technicalName);
        setTechnicalNameEdited(false);
        setShowCopyModal(true);
    };

    const handleConfirmCopy = async (technicalName?: string, displayName?: string) => {
        if (!copyToolId || !technicalName || duplicatingTool) {
            toast.error('Tool name is required to duplicate');
            return;
        }
        try {
            setDuplicatingTool(true);
            const res = await toolsApi.duplicate(copyToolId, displayName || '', technicalName);
            addTool(res.tool)
            toast.success('Tool duplicated successfully');
            refreshTagsFromTools(tools)
        } catch (error: any) {
            toast.error(`Failed to duplicate tool: ${error.message}`);
        } finally {
            setDuplicatingTool(false);
            setShowCopyModal(false);
            setCopyToolId(null);
        }
    };

    return (
        <>
          {canEdit && selectedIds.length > 0 && <div className={styles.bulkActionsRow} style={{ margin: '10px 0' }}>
                <button
                    className={styles.bulkDeleteBtn}
                    onClick={handleBulkDelete}
                    disabled={selectedIds.length === 0}
                >
                    Delete Selected
                </button>
            </div>}
            <div className={styles.table}>
                <div className={styles.tableHeader}>
                    <div>
                        {canEdit && <Checkbox
                        checked={allOnPageSelected}
                        onChange={e => handleSelectAll(e)}
                    />}
                    </div>
                    <div>Name</div>
                    <div>Description</div>
                    <div>Connector</div>
                    <div>Environment</div>
                    <div>Method</div>
                    <div>Status</div>
                    <div>Updated</div>
                    <div>Actions</div>
                </div>

                {filteredAndSortedTools.map((tool) => {
                    const con = connectors.find(p => p.id === tool.mainConnectionId);
                    const isConnectorActive = !!(con && con.isActive);
                    const isToolDisabledByConnector = !isConnectorActive;
                    const env = tool.environment || con?.environment as Environment | undefined;
                    const isMssql = con?.connectionTypeId === 7;
                    const validationError = isToolComplete(tool, isMssql) ? getToolValidationError(tool, isMssql) : null;

                    return (
                        <div key={tool.id} className={`${styles.tableRowWrap}`}>
                            <div className={styles.tableRow}>
                                <div>
                                    {canEdit && <Checkbox
                                        checked={selectedIds.includes(tool.id)}
                                        onChange={(checked) => handleSelect(tool.id, checked)}
                                    />}
                                </div>
                                <div className={`${styles.toolName} ${styles.truncatedText}`}>
                                    {!canEdit ? (
                                        <span className={styles.toolLinkDisabled} title="Cannot access tool: connector is inactive or not found">
                                            <TruncatedText
                                                text={tool.name}
                                                maxLength={
                                                    window.innerWidth < 500 ? 10 : window.innerWidth < 1500 ? 20 : 30
                                                }
                                                letReadMore={false}
                                                showFullOnHover={false}
                                            />
                                        </span>
                                    ) : (
                                        <Link to={`/toolslist/${tool.id}`} className={styles.toolLink}>
                                            <TruncatedText
                                                text={tool.title || tool.name}
                                                maxLength={
                                                    window.innerWidth < 500 ? 10 : window.innerWidth < 1500 ? 20 : 30
                                                }
                                                letReadMore={false}
                                                showFullOnHover={false}
                                            />
                                        </Link>
                                    )}
                                    <div className={styles.version}>
                                        v{tool.version}
                                        {isNewTool(tool) && <span className={styles.newBadge}>New</span>}
                                    </div>
                                </div>

                                <div className={styles.description}>
                                    <TruncatedText text={tool.description} maxLength={80} letReadMore={false} />
                                </div>
                                <div className={styles.provider}>
                                    {
                                        isToolDisabledByConnector || !con
                                            ? (
                                                <span>
                                                    N/A
                                                </span>
                                            )
                                            : con.connectionName
                                    }
                                </div>
                                <TagsEnv env={env} isToolDisabledByConnector={!con || isToolDisabledByConnector || !env} />
                                <div className={styles.method}>
                                    <span className={`${styles.methodBadge} ${styles[`method${tool.httpMethod}`]}`}>
                                        {tool.httpMethod}
                                    </span>
                                </div>

                                <div className={styles.status}>
                                    {user && hasMinRole(user?.role?.id, UserRole.ToolManager) ? (
                                        !isToolComplete(tool, isMssql) ? (
                                            <button
                                                className={`${styles.statusBadge} ${styles.statusNeedsComplete}`}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    window.location.href = `/toolslist/${tool.id}`;
                                                }}
                                            >
                                                <ClipboardPenLine size={16} color="#d66214" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                In progress
                                            </button>
                                        ) : validationError ? (
                                            <button
                                                className={`${styles.statusBadge} ${styles.statusNeedsComplete}`}
                                                title={validationError}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    window.location.href = `/toolslist/${tool.id}`;
                                                }}
                                            >
                                                <AlertTriangle size={16} color="#d66214" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                Missing
                                            </button>
                                        ) : isToolDisabledByConnector ? (
                                            <button
                                                disabled={true}
                                                className={`${styles.statusBadge} ${styles.statusDisabled}`}
                                                title="Tool is disabled because connector is inactive or not found"
                                            >
                                                <XCircle size={16} color="#b0b0b0" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                Disabled
                                            </button>
                                        ) : (
                                            <button
                                                disabled={!user || !hasMinRole(user?.role?.id, UserRole.ToolManager)}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    toggleToolStatus(tool.id, !tool.enabled)
                                                }}
                                                className={`${styles.statusBadge} ${tool.enabled
                                                    ? styles.statusEnabled
                                                    : styles.statusDisabled
                                                    }`}
                                            >
                                                {tool.enabled ? (
                                                    <>
                                                        <CheckCircle size={16} color="#45a148" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                        Enabled
                                                    </>
                                                ) : (
                                                    <>
                                                        <XCircle size={16} color="#b0b0b0" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                        Disabled
                                                    </>
                                                )}
                                            </button>
                                        )
                                    ) : (
                                        !isToolComplete(tool, isMssql) ? (
                                            <span className={`${styles.statusBadge} ${styles.statusNeedsComplete}`}>
                                                <ClipboardPenLine size={16} color="#d66214" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                In progress
                                            </span>
                                        ) : validationError ? (
                                            <span
                                                className={`${styles.statusBadge} ${styles.statusNeedsComplete}`}
                                                title={validationError}
                                            >
                                                <AlertTriangle size={16} color="#d66214" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                Missing
                                            </span>
                                        ) : (
                                            <span className={`${styles.statusBadge} ${isToolDisabledByConnector || !tool.enabled
                                                ? styles.statusDisabled
                                                : styles.statusEnabled
                                                }`}>
                                                {isToolDisabledByConnector || !tool.enabled ? (
                                                    <>
                                                        <XCircle size={16} color="#b0b0b0" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                        Disabled
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle size={16} color="#45a148" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                        Enabled
                                                    </>
                                                )}
                                            </span>
                                        )
                                    )}
                                </div>

                                <div className={styles.updated}>
                                    {new Date(tool.updated_at!).toLocaleDateString()}
                                </div>

                                <div className={styles.actions}>
                                    {user && hasMinRole(user?.role?.id, UserRole.ToolManager) ? (
                                        <>

                                            <Link to={`/toolslist/${tool.id}`} className={styles.actionBtn}>
                                                <SquarePen size={16} />
                                            </Link>
                                            <button
                                                onClick={() => handleDuplicate(tool.id, tool.title || '', tool.name)}
                                                className={`${styles.actionBtn} ${styles.duplicateBtn}`}
                                                title="Duplicate"
                                            >
                                                <Copy size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(tool.id)}
                                                className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                                title="Delete tool"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </>
                                    ) : (canView ? (
                                        isToolDisabledByConnector ? (
                                            <button
                                                disabled={true}
                                                className={`${styles.actionBtn} ${styles.actionBtnDisabled}`}
                                                title="Cannot view tool: connector is inactive or not found"
                                            >
                                                View
                                            </button>
                                        ) : (
                                            <Link to={`/toolslist/${tool.id}`} className={styles.actionBtn}>
                                                View
                                            </Link>
                                        )
                                    ) : null)}
                                </div>
                            </div>
                            {tool.tags && tool.tags.length > 0 && (
                                <div className={styles.toolTagsRow}>
                                    {(expandedTagToolIds.has(tool.id) ? tool.tags : tool.tags.slice(0, 3)).map(tag => (
                                        <button
                                            type="button"
                                            key={tag.id ?? tag.name}
                                            className={styles.tagBadge}
                                            onClick={() => onTagClick && onTagClick(tag.name)}
                                            title={`Filter by tag: ${tag.name}`}
                                        >{tag.name}</button>
                                    ))}
                                    {tool.tags.length > 3 && (
                                        <button
                                            type="button"
                                            className={styles.tagBadgeButton}
                                            onClick={() => {
                                                setExpandedTagToolIds(prev => {
                                                    const next = new Set(prev)
                                                    if (next.has(tool.id)) next.delete(tool.id); else next.add(tool.id)
                                                    return next
                                                })
                                            }}
                                            title={expandedTagToolIds.has(tool.id) ? 'Show fewer tags' : 'Show all tags'}
                                        >{expandedTagToolIds.has(tool.id) ? 'Show less' : `+${tool.tags.length - 3} more`}</button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                <InputModal
                    open={showDeleteModal}
                    title={deleteIds.length > 1 ? 'Delete Tools' : 'Delete Tool'}
                    confirmText="Delete"
                    cancelText="Cancel"
                    showInput={false}
                    message={deleteIds.length > 1
                        ? `Are you sure you want to delete ${deleteIds.length} tools? This action cannot be undone.`
                        : 'Are you sure you want to delete this tool? This action cannot be undone.'}
                    loading={deletingTools}
                    loadingText="Deleting..."
                    onConfirm={handleConfirmDelete}
                    onCancel={() => {
                        if (!deletingTools) {
                            setShowDeleteModal(false);
                            setDeleteIds([]);
                        }
                    }}
                />
                <InputModal
                    open={showCopyModal}
                    title="Duplicate Tool"
                    placeholder="Enter new tool display name and technical name"
                    confirmText="Duplicate"
                    cancelText="Cancel"
                    showInput={false}
                    loading={duplicatingTool}
                    loadingText="Duplicating..."
                    onConfirm={() => handleConfirmCopy(technicalName, displayName)}
                    onCancel={() => {
                        if (!duplicatingTool) {
                            setShowCopyModal(false);
                        }
                    }}
                >
                    <SecureInput
                        fieldType="name"
                        label="Display Name"
                        value={displayName}
                        placeholder="Enter new tool name"
                        onChange={(newValue) => {
                            setDisplayName(newValue);
                            if (!technicalNameEdited) {
                                setTechnicalName(
                                    newValue
                                        .trim()
                                        .toLowerCase()
                                        .replace(/[^a-z0-9\s_-]/g, '')
                                        .replace(/[\s-]+/g, '_')
                                );
                            }
                        }}
                        className={styles.input}
                    />
                    <SecureInput
                        fieldType="text"
                        label="Technical Name"
                        description="Use lowercase letters, numbers, and underscores only."
                        value={technicalName}
                        placeholder="e.g. supplier_activity_tracking"
                        onChange={(newValue) => {
                            setTechnicalNameEdited(true);
                            setTechnicalName(
                                newValue
                                    .toLowerCase()
                                    .replace(/\s+/g, '_')
                                    .replace(/[^a-z0-9_]/g, '')
                            );
                        }}
                        className={styles.input}
                    />
                </InputModal>
                <Pagination
                    page={page}
                    pageCount={pageCount}
                    setPage={setPage}
                />
            </div>
        </>
    );
};

export default ToolsTable;
